// app/javascript/controllers/auto_scroll_controller.js
import { Controller } from "@hotwired/stimulus"
import { post } from "@rails/request.js"

export default class extends Controller {
  static targets = ["messagesContainer", "loadingIndicator"]
  static values = { url: String }

  connect() {
    this.scrollToBottom()
    this.observe_scrollToBottom()

    this.isLoading = false; // Flag to prevent multiple simultaneous loads
    this.hasMoreMessages = true; // Assume there are more messages initially

    // Add scroll event listener
    this.messagesContainerTarget.addEventListener("scroll", this.onScroll.bind(this));

    // Initialize MutationObserver to watch for new messages being added
    this.observer = new MutationObserver(this.handleMutations.bind(this));
    this.observer.observe(this.messagesContainerTarget, { childList: true });

    // Mark messages as read when the chat is viewed
    this.markMessagesAsRead();
  }

  handleMutations(mutationsList) {
    // This is called when new messages are added (e.g., via Turbo Stream prepend/append)
    // or removed. We re-apply grouping to ensure consistency.
    // this.applyGroupingAndStyling();
    // Also mark as read if new messages appear while user is viewing
    this.markMessagesAsRead();
  }


  onScroll() {
    // Check if the user has scrolled to the very top (or very near it)
    if (this.messagesContainerTarget.scrollTop < 20 && !this.isLoading && this.hasMoreMessages) {
      this.loadMoreMessages();
    }
  }

  async loadMoreMessages() {
    this.isLoading = true;
    this.loadingIndicatorTarget.classList.remove("hidden"); // Show loading indicator

    const oldestMessage = this.messagesContainerTarget.querySelector(".message-container");
    const beforeId = oldestMessage ? oldestMessage.dataset.messageId : null; // Get the ID of the oldest message

    // Store current scroll position relative to content height
    const oldScrollHeight = this.messagesContainerTarget.scrollHeight;
    const oldScrollTop = this.messagesContainerTarget.scrollTop;

    try {
      // Fetch older messages using Turbo Stream format
      const response = await fetch(`${this.urlValue}?before_id=${beforeId}&format=turbo_stream`);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const html = await response.text();

      // Check if any messages were returned
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      const turboStreamElements = doc.querySelectorAll('turbo-stream');
      let newMessagesCount = 0;

      turboStreamElements.forEach(streamElement => {
        if (streamElement.getAttribute('action') === 'prepend' && streamElement.getAttribute('target') === 'messages') {
          const template = streamElement.querySelector('template');
          if (template) {
            const content = template.content;
            const messageElements = content.querySelectorAll('.message-container');
            newMessagesCount += messageElements.length;

            // Manually prepend the content to the messages container
            this.messagesContainerTarget.prepend(content);
          }
        }
      });

      if (newMessagesCount === 0) {
        this.hasMoreMessages = false; // No more messages to load
      } else {
        // Adjust scroll position to maintain user's view
        const newScrollHeight = this.messagesContainerTarget.scrollHeight;
        this.messagesContainerTarget.scrollTop = oldScrollTop + (newScrollHeight - oldScrollHeight);
      }

    } catch (error) {
      console.error("Failed to load older messages:", error);
      // Optionally display an error message to the user
    } finally {
      this.isLoading = false;
      this.loadingIndicatorTarget.classList.add("hidden"); // Hide loading indicator
    }
  }

  observe_scrollToBottom() {
    this.observer = new MutationObserver(() => {
      if (this.shouldAutoScroll()) {
        this.scrollToBottom()
      }
    })

    this.observer.observe(this.element, {
      childList: true,
      subtree: true
    })
  }

  disconnect() {
    if (this.observer) {
      this.observer.disconnect()
    }

    this.messagesContainerTarget.removeEventListener("scroll", this.onScroll.bind(this));
  }

  scrollToBottom() {
    // Scroll to the new messages separator on initial load, or to the bottom if no separator
    const unreadSeparator = this.element.querySelector("#unread-separator");
    if (unreadSeparator) {
      console.log('scrolling to separator', unreadSeparator.offsetTop - 300);
      // Scroll to the separator, with a small offset for visibility
      this.element.scrollTop = unreadSeparator.offsetTop - 300;
      console.log('scrolled to', this.element.scrollTop);
    } else {
      console.log('scrolling to end');
      // If no unread separator, scroll to the bottom
      this.element.scrollTop = this.element.scrollHeight;
    }
  }

  shouldAutoScroll() {
    const threshold = 100
    return (this.element.scrollHeight - this.element.clientHeight - this.element.scrollTop) < threshold
  }

  async markMessagesAsRead() {
    const roomId = this.data.get("roomId"); // Get room ID from data attribute
    if (!roomId) {
      console.warn("Room ID not found for marking messages as read. Cannot mark as read.");
      return;
    }

    const url = `${this.urlValue}/mark_as_read`;
    try {
      const response = await post(url, {'responseKind': 'json'});

      if (!response.ok) {
        console.error(`Failed to mark messages as read: HTTP status ${response.status}`);
      } else {
        console.log("Messages marked as read.");
      }
    } catch (error) {
      console.error("Error marking messages as read:", error);
    }
  }

}
